import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, isToday, setYear } from 'date-fns';
import { cn } from '../lib/utils';

interface CalendarProps {
  selectedDate: Date;
  onDateSelect: (date: Date) => void;
  minDate?: Date;
  maxDate?: Date;
}

export function Calendar({ selectedDate, onDateSelect, minDate, maxDate }: CalendarProps) {
  const [currentMonth, setCurrentMonth] = useState(selectedDate || new Date());
  const [showYearSelect, setShowYearSelect] = useState(false);
  
  const daysInMonth = eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth)
  });

  const startingDayIndex = startOfMonth(currentMonth).getDay();
  const endingDayIndex = endOfMonth(currentMonth).getDay();

  const previousMonth = () => setCurrentMonth(prev => subMonths(prev, 1));
  const nextMonth = () => setCurrentMonth(prev => addMonths(prev, 1));

  const isDateSelectable = (date: Date) => {
    if (minDate && date < minDate) return false;
    if (maxDate && date > maxDate) return false;
    return true;
  };

  const handleYearSelect = (year: number) => {
    const newDate = setYear(currentMonth, year);
    setCurrentMonth(newDate);
    setShowYearSelect(false);
  };

  const currentYear = currentMonth.getFullYear();
  const years = Array.from({ length: 6 }, (_, i) => currentYear + i - 2);

  return (
    <div className="p-4 bg-[#1a2236] rounded-lg border border-white/10">
      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={previousMonth}
          className="p-2 hover:bg-white/5 rounded-full transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="relative">
          <button
            onClick={() => setShowYearSelect(!showYearSelect)}
            className="text-lg font-semibold hover:text-primary transition-colors"
          >
            {format(currentMonth, 'MMMM yyyy')}
          </button>
          
          {showYearSelect && (
            <div className="absolute top-full mt-2 z-20 bg-[#1a2236] border border-white/10 rounded-lg shadow-xl py-2 w-32">
              {years.map(year => (
                <button
                  key={year}
                  onClick={() => handleYearSelect(year)}
                  className={cn(
                    "w-full px-4 py-2 text-left hover:bg-white/5 transition-colors",
                    year === currentYear && "text-primary font-semibold"
                  )}
                >
                  {year}
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          onClick={nextMonth}
          className="p-2 hover:bg-white/5 rounded-full transition-colors"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1">
        {/* Day Headers */}
        {['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].map(day => (
          <div key={day} className="text-center text-xs font-medium text-gray-400 py-2">
            {day}
          </div>
        ))}

        {/* Empty cells for previous month */}
        {Array.from({ length: startingDayIndex }).map((_, index) => (
          <div key={`empty-start-${index}`} className="aspect-square" />
        ))}

        {/* Days of the month */}
        {daysInMonth.map(date => {
          const isSelected = selectedDate && isSameDay(date, selectedDate);
          const isCurrentDay = isToday(date);
          const selectable = isDateSelectable(date);

          return (
            <button
              key={date.toISOString()}
              onClick={() => selectable && onDateSelect(date)}
              disabled={!selectable}
              className={cn(
                "aspect-square relative flex items-center justify-center text-sm transition-colors",
                selectable ? "hover:bg-primary/20" : "opacity-50 cursor-not-allowed",
                isSelected && "bg-primary text-white hover:bg-primary",
                !isSelected && isCurrentDay && "text-primary font-semibold",
                "rounded-full"
              )}
            >
              <time dateTime={format(date, 'yyyy-MM-dd')}>
                {format(date, 'd')}
              </time>
              {isCurrentDay && !isSelected && (
                <div className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-primary rounded-full" />
              )}
            </button>
          );
        })}

        {/* Empty cells for next month */}
        {Array.from({ length: 6 - endingDayIndex }).map((_, index) => (
          <div key={`empty-end-${index}`} className="aspect-square" />
        ))}
      </div>
    </div>
  );
}